import React from 'react';
import { BalanceRing } from '../dashboard/BalanceRing';

// Fixed Avatar component with proper visible styling
const SIZES = {
  sm: { box: 28, font: 10 },
  md: { box: 36, font: 13 },
  lg: { box: 44, font: 15 },
};

export function Avatar({ employee, size = 'md' }) {
  const { box, font } = SIZES[size] || SIZES.md;

  // Get initials from real user data
  const getInitials = () => {
    if (!employee) return '?';
    if (employee.avatar) return employee.avatar; // mock data fallback
    
    // Try to get firstName and lastName
    if (employee.firstName && employee.lastName) {
      return (employee.firstName[0] + employee.lastName[0]).toUpperCase();
    }
    
    // Fallback to full name
    if (employee.name) {
      const nameParts = employee.name.trim().split(' ');
      if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
      }
      return employee.name[0].toUpperCase();
    }
    
    return '?';
  };

  // Generate consistent, visible color based on employee name
  const getAvatarColor = (name) => {
    const colors = [
      '#1C1A14', '#2E2A20', '#3A362B', '#4A4234',
      '#5A4F3C', '#645B4A', '#6E4C00', '#7A5500',
      '#8A5F00', '#5C4320', '#4B3A1C', '#3D3020',
      '#6B4E1F', '#54462E', '#463A28', '#2B2419'
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // If user has a profile image, show it
  if (employee?.profileImageUrl) {
    return (
      <img
        src={employee.profileImageUrl}
        alt={employee.name || employee.firstName}
        style={{
          width: box, 
          height: box, 
          borderRadius: '50%',
          objectFit: 'cover', 
          flexShrink: 0,
        }}
      />
    );
  }

  // Show colored avatar with initials
  const avatarColor = getAvatarColor(employee?.name || employee?.firstName || '');
  const initials = getInitials();
  
  return (
    <div
      style={{
        width: box, 
        height: box, 
        borderRadius: '50%',
        backgroundColor: avatarColor, // This will now be a visible color
        color: '#FBF4E2', // Cream text on ink-toned background
        fontSize: font, 
        fontWeight: 600,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexShrink: 0, 
        userSelect: 'none',
        textTransform: 'uppercase',
        boxShadow: 'inset 0 0 0 1px var(--avatar-ring), 0 1px 3px rgba(0,0,0,0.1)', // Optional: adds slight shadow
      }}
    >
      {initials}
    </div>
  );
}

export function EmployeeCard({ employee, onSelect }) {
  const usedPct = Math.round((employee.used / employee.balance) * 100);
  const remaining = employee.balance - employee.used;
  
  console.log("Employees", employee);
  
  return (
    <div
      className="card cursor-pointer hover:-translate-y-0.5 transition-all duration-200"
      style={{ boxShadow: 'none' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(28,26,20,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
      onClick={() => onSelect(employee)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Avatar employee={employee} size="md" />
          <div>
            <p className="text-sm font-semibold text-sand-900">{employee.name}</p>
            <p className="text-xs text-sand-400">{employee.role}</p>
          </div>
        </div>
        <BalanceRing used={employee.used} balance={employee.balance} size={52} />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full"
          style={{ background: '#1C1A1412', color: '#1C1A14' }}
        >
          {employee.dept}
        </span>
        {employee.pending > 0 && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{ background: '#fff1c2', color: '#6e4c00' }}>
            {employee.pending} pending
          </span>
        )}
      </div>

      <div>
        <div className="flex justify-between mb-1" style={{ fontSize: 10, color: '#b5a889' }}>
          <span>{employee.used}d used</span>
          <span>{remaining}d left</span>
        </div>
        <div className="h-1.5 bg-sand-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${usedPct}%`,
              background: usedPct > 80 ? '#dc2626' : usedPct > 60 ? '#c98a00' : '#1C1A14',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function MembersList({ employees, onSelect }) {
  const [selected, setSelected] = React.useState([]);

  const toggleAll = (checked) =>
    setSelected(checked ? employees.map(e => e.id) : []);

  const toggleOne = (id) =>
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  // Helper function to get initials from first name and last name
  const getInitials = (employee) => {
    if (employee.firstName && employee.lastName) {
      return (employee.firstName[0] + employee.lastName[0]).toUpperCase();
    }
    if (employee.name) {
      const nameParts = employee.name.trim().split(' ');
      if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
      }
      return employee.name[0].toUpperCase();
    }
    return '?';
  };

  // Generate consistent color for avatar
  const getAvatarColor = (name) => {
    const colors = [
      '#1C1A14', '#2E2A20', '#3A362B', '#4A4234',
      '#5A4F3C', '#645B4A', '#6E4C00', '#7A5500',
      '#8A5F00', '#5C4320', '#4B3A1C', '#3D3020',
      '#6B4E1F', '#54462E', '#463A28', '#2B2419'
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      border: '1px solid #eadfc6',
      overflow: 'hidden',
    }}>

      {/* Column headers */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 20px',
        borderBottom: '1px solid #f3ead4',
        background: '#fbf4e2',
      }}>
        <input
          type="checkbox"
          checked={selected.length === employees.length && employees.length > 0}
          onChange={e => toggleAll(e.target.checked)}
          style={{ marginRight: 16, width: 15, height: 15, cursor: 'pointer', accentColor: '#1C1A14' }}
        />
        <div style={{ width: 36, marginRight: 14 }} />
        <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: '#857a63', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</span>
        <span style={{ flex: 1.4, fontSize: 11, fontWeight: 600, color: '#857a63', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</span>
        <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: '#857a63', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Department</span>
        <div style={{ width: 28 }} />
      </div>

      {/* Rows */}
      {employees.map(employee => {
        const initials = getInitials(employee);
        const avatarColor = getAvatarColor(employee.name || employee.email || '');
        
        return (
          <div
            key={employee.id}
            onClick={() => onSelect(employee)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '11px 20px',
              borderBottom: '1px solid #f8f1df',
              cursor: 'pointer',
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#fbf4e2'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
          >
            <input
              type="checkbox"
              checked={selected.includes(employee.id)}
              onChange={e => { e.stopPropagation(); toggleOne(employee.id); }}
              onClick={e => e.stopPropagation()}
              style={{ marginRight: 16, width: 15, height: 15, cursor: 'pointer', accentColor: '#1C1A14' }}
            />

            {/* Avatar with visible colors */}
            <div style={{ marginRight: 14, flexShrink: 0, position: 'relative' }}>
              {employee.profileImageUrl ? (
                <img
                  src={employee.profileImageUrl}
                  alt={initials}
                  style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: 36, 
                  height: 36, 
                  borderRadius: '50%',
                  background: avatarColor, // Visible color background
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: 13, 
                  fontWeight: 700, 
                  color: '#FBF4E2', // Cream text for contrast
                  flexShrink: 0,
                  textTransform: 'uppercase',
                }}>
                  {initials}
                </div>
              )}
              {employee.verified && (
                <div style={{
                  position: 'absolute', 
                  bottom: 0, 
                  right: 0,
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%',
                  background: '#16a34a', 
                  border: '2px solid #fff',
                }} />
              )}
            </div>

            {/* Name */}
            <span style={{
              flex: 1,
              fontSize: 13,
              fontWeight: 600,
              color: '#1C1A14',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {employee.name}
            </span>

            {/* Email */}
            <span style={{
              flex: 1.4,
              fontSize: 13,
              color: '#857a63',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {employee.email}
            </span>

            {/* Department */}
            <span style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <span style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#1C1A14',
                background: '#1C1A1410',
                padding: '2px 10px',
                borderRadius: 20,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                display: 'inline-block',
              }}>
                {employee.dept ?? '—'}
              </span>
            </span>

            {/* Actions */}
            <button
              onClick={e => e.stopPropagation()}
              style={{
                width: 28, 
                height: 28,
                background: 'none',
                border: '1px solid #eadfc6',
                borderRadius: 8,
                cursor: 'pointer',
                color: '#857a63',
                fontSize: 14,
                lineHeight: 1,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexShrink: 0,
              }}
              onMouseEnter={e => { 
                e.currentTarget.style.background = '#f3ead4'; 
                e.currentTarget.style.color = '#1C1A14'; 
              }}
              onMouseLeave={e => { 
                e.currentTarget.style.background = 'none'; 
                e.currentTarget.style.color = '#857a63'; 
              }}
            >
              ···
            </button>
          </div>
        );
      })}

      {employees.length === 0 && (
        <p style={{ fontSize: 13, color: '#b5a889', textAlign: 'center', padding: '40px 0' }}>
          No users found.
        </p>
      )}
    </div>
  );
}